import { Menu } from 'antd';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import UilEllipsisV from '@iconscout/react-unicons/icons/uil-ellipsis-v';
import propTypes from 'prop-types';
// Gray = default, Blue = hover/active
import { ReactComponent as BasicProfileGray } from '../assets/icons/basicprofile-gray.svg';
import { ReactComponent as BasicProfileBlue } from '../assets/icons/basicprofile-blue.svg';
import { ReactComponent as CalculatorGray } from '../assets/icons/calculator-gray.svg';
import { ReactComponent as CalculatorBlue } from '../assets/icons/calculator-blue.svg';
import { ReactComponent as RequestSessionGray } from '../assets/icons/requestsession-gray.svg';
import { ReactComponent as RequestSessionBlue } from '../assets/icons/requestsession-blue.svg';
import { ReactComponent as PaymentsGray } from '../assets/icons/payments-gray.svg';
import { ReactComponent as PaymentsBlue } from '../assets/icons/payments-blue.svg';
import { ReactComponent as MerchantGray } from '../assets/icons/merchant-gray.svg';
import { ReactComponent as MerchantBlue } from '../assets/icons/merchant-blue.svg';
import { ReactComponent as PackageGray } from '../assets/icons/package-gray.svg';
import { ReactComponent as PackageBlue } from '../assets/icons/package-blue.svg';
import { ReactComponent as PaymentGray } from '../assets/icons/payment-gray.svg';
import { ReactComponent as PaymentBlue } from '../assets/icons/payment-blue.svg';
import { ReactComponent as SourceGray } from '../assets/icons/source-gray.svg';
import { ReactComponent as SourceBlue } from '../assets/icons/source-blue.svg';
import { ReactComponent as MerchantRequestGray } from '../assets/icons/merchantrequest-gray.svg';
import { ReactComponent as MerchantRequestBlue } from '../assets/icons/merchantrequest-blue.svg';
import { ReactComponent as StaffGray } from '../assets/icons/staff-gray.svg';
import { ReactComponent as StaffBlue } from '../assets/icons/staff-blue.svg';
import { ReactComponent as ReferralGray } from '../assets/icons/referral-gray.svg';
import { ReactComponent as ReferralBlue } from '../assets/icons/referral-blue.svg';

const iconStyle = { width: 25, height: 25 };

function SidebarIcon({ GrayIcon, BlueIcon, isActive, isHover }) {
  const showBlue = isActive || isHover;
  const Icon = showBlue ? BlueIcon : GrayIcon;
  return <Icon style={iconStyle} />;
}
SidebarIcon.propTypes = {
  GrayIcon: propTypes.elementType.isRequired,
  BlueIcon: propTypes.elementType.isRequired,
  isActive: propTypes.bool,
  isHover: propTypes.bool,
};
SidebarIcon.defaultProps = {
  isActive: false,
  isHover: false,
};

function MenuItems({ toggleCollapsed }) {
  function getItem(label, key, icon, children, type) {
    return {
      key,
      icon,
      children,
      label,
      type,
    };
  }
  const { topMenu } = useSelector((state) => ({
    topMenu: state.ChangeLayoutMode.topMenu,
  }));
  // const { role } = useSelector((state) => ({
  //   role: state.auth.role,
  // }));

  // Use role from auth.user (login/switch API) or from me API (merchant/me) so sidebar is correct after account switch
  const role = useSelector(
    (state) => state?.auth?.user?.role || state?.auth?.me?.user?.roles?.[0] || state?.auth?.me?.roles?.[0],
  );

  const UsersProfile = useSelector((state) => state?.auth?.me);
  const permissionLabels = useSelector((state) => state?.auth?.me?.permission_labels || []);

  const isCommissionCalculatorEnabled = UsersProfile?.package?.is_commission_calculator_enable;

  const language = useSelector((state) => state?.auth?.language);
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState([]);
  const [currentPath, setCurrentPath] = useState(location.pathname);
  const [hoveredKey, setHoveredKey] = useState(null);
  const onOpenChange = (keys) => {
    setOpenKeys(keys[keys.length - 1] !== 'recharts' ? [keys.length && keys[keys.length - 1]] : keys);
  };
  const onClick = (item) => {
    if (item.keyPath.length === 1) setOpenKeys([]);
  };
  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location.pathname]);
  useEffect(() => {
    if (!topMenu) {
      setOpenKeys([currentPath]);
    } else {
      setOpenKeys([]);
    }
  }, [topMenu, currentPath]);
  const menuLinks = [
    {
      label: language?.content?.BASIC_PROFILE_DETAILS || 'Basic Profile Details',
      path: `/profile`,
      iconGray: BasicProfileGray,
      iconBlue: BasicProfileBlue,
      roles: ['merchant'],
      subRole: [],
      isEnabled: true,
    },
    {
      label: language?.content?.BASIC_PROFILE_DETAILS || 'Basic Profile Details',
      path: `/staff-profile`,
      iconGray: BasicProfileGray,
      iconBlue: BasicProfileBlue,
      roles: ['staff'], // 'staff', 'sales_agent'
      subRole: [],
      isEnabled: true,
    },
    {
      label: language?.content?.BASIC_PROFILE_DETAILS || 'Basic Profile Details',
      path: `/sales-agent-profile`,
      iconGray: BasicProfileGray,
      iconBlue: BasicProfileBlue,
      roles: ['sales_agent'], // 'staff', 'sales_agent'
      subRole: [],
      isEnabled: true,
    },
    {
      label: language?.content?.COMMISSION_CALCULATOR || 'Commission Calculator',
      path: `/commission-calculator`,
      iconGray: CalculatorGray,
      iconBlue: CalculatorBlue,
      roles: ['merchant'],
      subRole: [],
      isEnabled: isCommissionCalculatorEnabled,
    },
    {
      label: language?.content?.REQUEST_SESSION || 'Request Session',
      path: `/request-session`,
      iconGray: RequestSessionGray,
      iconBlue: RequestSessionBlue,
      roles: ['merchant'],
      subRole: [],
      isEnabled: true,
    },
    {
      label: language?.content?.MERCHANTS || 'Merchants',
      path: `/merchants`,
      iconGray: MerchantGray,
      iconBlue: MerchantBlue,
      roles: ['super_admin', 'staff'],
      subRole: ['merchant-management'],
      isEnabled: true,
    },
    {
      label: language?.content?.MERCHANTS || 'Merchants',
      path: `/sales-agent-merchants`,
      pathPrefixes: ['/sales-agent-merchants'],
      iconGray: MerchantGray,
      iconBlue: MerchantBlue,
      roles: ['sales_agent'],
      subRole: [],
      isEnabled: true,
    },
    {
      label: language?.content?.PACKAGES || 'Packages',
      path: `/packages`,
      pathPrefixes: ['/packages', '/create-package'],
      iconGray: PackageGray,
      iconBlue: PackageBlue,
      roles: ['super_admin', 'staff'],
      subRole: ['package-management'],
      isEnabled: true,
    },
    {
      label: language?.content?.PAYMENTS || 'Payments',
      path: `/payments`,
      iconGray: PaymentGray,
      iconBlue: PaymentBlue,
      roles: ['super_admin', 'merchant'],
      subRole: [],
      isEnabled: true,
    },
    {
      label: language?.content?.SOURCES || 'Sources',
      path: `/sources`,
      iconGray: SourceGray,
      iconBlue: SourceBlue,
      roles: ['super_admin'],
      subRole: [],
      isEnabled: true,
    },
    {
      label: language?.content?.SALES_AGENTS || 'Sales Agents',
      path: `/sales-agents`,
      iconGray: BasicProfileGray,
      iconBlue: BasicProfileBlue,
      roles: ['super_admin', 'staff'],
      subRole: ['sales-agent-management'],
      isEnabled: true,
    },
    {
      label: language?.content?.MERCHANT_REQUEST || 'Merchant Requests',
      path: `/merchant/requests`,
      iconGray: MerchantRequestGray,
      iconBlue: MerchantRequestBlue,
      roles: ['super_admin', 'staff'],
      subRole: ['merchant-approval'],
      isEnabled: true,
    },
    {
      label: language?.content?.REFERRALS || 'Referrals',
      path: `/referrals`,
      iconGray: ReferralGray,
      iconBlue: ReferralBlue,
      roles: ['merchant', 'super_admin', 'staff'],
      subRole: ['referral-management'],
      isEnabled: true,
    },
    {
      label: language?.content?.STAFFS || 'Staffs',
      path: `/staffs`,
      iconGray: StaffGray,
      iconBlue: StaffBlue,
      roles: ['super_admin'], // 'staff', 'sales_agent'
      subRole: [],
      isEnabled: true,
    },
  ];

  const staffPermissionSlugs = new Set(
    (Array.isArray(permissionLabels) ? permissionLabels : [])
      .map((permission) => {
        if (typeof permission === 'string') return permission;
        if (permission && typeof permission === 'object') return permission.slug || permission.label || '';
        return '';
      })
      .map((value) =>
        String(value || '')
          .trim()
          .toLowerCase()
          .replace(/\s+/g, '-'),
      )
      .filter(Boolean),
  );

  const filteredLinks = menuLinks.filter((link) => {
    if (!link.roles.includes(role) || !link.isEnabled) return false;
    if (role !== 'staff') return true;
    if (!Array.isArray(link.subRole) || link.subRole.length === 0) return true;
    return link.subRole.some((requiredSubRole) =>
      staffPermissionSlugs.has(String(requiredSubRole || '').toLowerCase()),
    );
  });
  const selectedKeys = filteredLinks
    .filter((link) => {
      if (currentPath === '/' && link.path === '/') return true;
      if (link.pathPrefixes) {
        return link.pathPrefixes.some((prefix) => currentPath.startsWith(prefix));
      }
      return currentPath.startsWith(link.path) && link.path !== '/';
    })
    .map((link) => link.path);
  const items = filteredLinks.map((link) =>
    getItem(
      <NavLink onClick={toggleCollapsed} to={link.path}>
        {link.label}
      </NavLink>,
      link.path,
      !topMenu && (
        <NavLink
          className="menuItem-iocn"
          to={link.path}
          onMouseEnter={() => setHoveredKey(link.path)}
          onMouseLeave={() => setHoveredKey(null)}
        >
          <SidebarIcon
            GrayIcon={link.iconGray}
            BlueIcon={link.iconBlue}
            isActive={selectedKeys.includes(link.path)}
            isHover={hoveredKey === link.path}
          />
        </NavLink>
      ),
    ),
  );
  return (
    <Menu
      onOpenChange={onOpenChange}
      onClick={onClick}
      mode={!topMenu || window.innerWidth <= 991 ? 'inline' : 'horizontal'}
      defaultOpenKeys={!topMenu ? [openKeys[0] || 'dashboard'] : []}
      overflowedIndicator={<UilEllipsisV />}
      openKeys={openKeys}
      selectedKeys={selectedKeys} // Use the selectedKeys derived from currentPath
      items={items}
    />
  );
}
MenuItems.propTypes = {
  toggleCollapsed: propTypes.func,
};
export default MenuItems;
